import React, { useEffect, useState, Children } from 'react';
import { Search, SlidersHorizontal, ChevronDown, MapPin, Bed, Bath, Ruler, Heart, Plus, X, Upload, Image as ImageIcon, Check, Navigation, Camera, DollarSign, Home, Building2 } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { TopBar } from '../components/TopBar';
import { MainTabs } from '../components/MainTabs';
interface PropertiesPageProps {
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onClientsClick?: () => void;
  onDashboardClick?: () => void;
  onProfileClick?: () => void;
  onPropertyClick?: (propertyId: number) => void;
}
type PropertyType = 'rent' | 'sale';
interface Property {
  id: number;
  title: string;
  price: string;
  priceValue: number;
  location: string;
  beds: number;
  baths: number;
  area: string;
  areaValue: number;
  image: string;
  type: PropertyType;
  propertyType: string;
  isFavorite?: boolean;
}
export function PropertiesPage({
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onClientsClick,
  onDashboardClick,
  onProfileClick,
  onPropertyClick
}: PropertiesPageProps) {
  const [activeTab, setActiveTab] = useState<PropertyType>('sale');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [favorites, setFavorites] = useState<number[]>([]);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  // Advanced Filter State
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [filterBeds, setFilterBeds] = useState('');
  const [filterBaths, setFilterBaths] = useState('');
  const [filterPropertyType, setFilterPropertyType] = useState('');
  const [filterMinArea, setFilterMinArea] = useState('');
  const [filterMaxArea, setFilterMaxArea] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  // Add Property Form State
  const [transactionType, setTransactionType] = useState('Sale');
  const [location, setLocation] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [propertyGroup, setPropertyGroup] = useState('Residential');
  const [propertyType, setPropertyType] = useState('Apartments');
  const [propertyStage, setPropertyStage] = useState('New');
  const [propertyView, setPropertyView] = useState('');
  const [finishedType, setFinishedType] = useState('');
  const [propertySize, setPropertySize] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [inCompound, setInCompound] = useState(false);
  const [isReadyToMove, setIsReadyToMove] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState('');
  const [isInsurance, setIsInsurance] = useState(false);
  const [isInstallments, setIsInstallments] = useState(false);
  const [description, setDescription] = useState('');
  const [propertyImages, setPropertyImages] = useState<string[]>([]);
  const [selectedPropertyAmenities, setSelectedPropertyAmenities] = useState<string[]>([]);
  const [selectedCommunityAmenities, setSelectedCommunityAmenities] = useState<string[]>([]);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Mock properties data with Unsplash images
  const [properties] = useState<Property[]>([
  // Sale Properties
  {
    id: 1,
    title: 'Luxury Villa with Pool',
    price: '8,500,000 EGP',
    priceValue: 8500000,
    location: 'New Cairo, 5th Settlement',
    beds: 5,
    baths: 4,
    area: '450 sqm',
    areaValue: 450,
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80',
    type: 'sale',
    propertyType: 'Villa'
  }, {
    id: 2,
    title: 'Modern Apartment',
    price: '3,200,000 EGP',
    priceValue: 3200000,
    location: 'New Cairo, Waterway',
    beds: 3,
    baths: 2,
    area: '185 sqm',
    areaValue: 185,
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80',
    type: 'sale',
    propertyType: 'Apartment'
  }, {
    id: 3,
    title: 'Penthouse with View',
    price: '12,000,000 EGP',
    priceValue: 12000000,
    location: 'New Cairo, Madinaty',
    beds: 4,
    baths: 3,
    area: '320 sqm',
    areaValue: 320,
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80',
    type: 'sale',
    propertyType: 'Penthouse'
  }, {
    id: 4,
    title: 'Duplex in Compound',
    price: '5,800,000 EGP',
    priceValue: 5800000,
    location: 'New Cairo, Hyde Park',
    beds: 4,
    baths: 3,
    area: '280 sqm',
    areaValue: 280,
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
    type: 'sale',
    propertyType: 'Duplex'
  }, {
    id: 5,
    title: 'Townhouse with Garden',
    price: '6,500,000 EGP',
    priceValue: 6500000,
    location: 'New Cairo, Rehab',
    beds: 4,
    baths: 3,
    area: '300 sqm',
    areaValue: 300,
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    type: 'sale',
    propertyType: 'Townhouse'
  }, {
    id: 6,
    title: 'Studio Apartment',
    price: '1,800,000 EGP',
    priceValue: 1800000,
    location: 'New Cairo, Waterway',
    beds: 1,
    baths: 1,
    area: '65 sqm',
    areaValue: 65,
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
    type: 'sale',
    propertyType: 'Studio'
  },
  // Rent Properties
  {
    id: 7,
    title: 'Furnished Apartment',
    price: '25,000 EGP/month',
    priceValue: 25000,
    location: 'New Cairo, 5th Settlement',
    beds: 3,
    baths: 2,
    area: '180 sqm',
    areaValue: 180,
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
    type: 'rent',
    propertyType: 'Apartment'
  }, {
    id: 8,
    title: 'Luxury Villa for Rent',
    price: '80,000 EGP/month',
    priceValue: 80000,
    location: 'New Cairo, Hyde Park',
    beds: 5,
    baths: 4,
    area: '400 sqm',
    areaValue: 400,
    image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800&q=80',
    type: 'rent',
    propertyType: 'Villa'
  }, {
    id: 9,
    title: 'Modern Studio',
    price: '12,000 EGP/month',
    priceValue: 12000,
    location: 'New Cairo, Waterway',
    beds: 1,
    baths: 1,
    area: '70 sqm',
    areaValue: 70,
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
    type: 'rent',
    propertyType: 'Studio'
  }, {
    id: 10,
    title: 'Family Apartment',
    price: '35,000 EGP/month',
    priceValue: 35000,
    location: 'New Cairo, Madinaty',
    beds: 3,
    baths: 2,
    area: '200 sqm',
    areaValue: 200,
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80',
    type: 'rent',
    propertyType: 'Apartment'
  }, {
    id: 11,
    title: 'Penthouse Rental',
    price: '60,000 EGP/month',
    priceValue: 60000,
    location: 'New Cairo, Waterway',
    beds: 4,
    baths: 3,
    area: '280 sqm',
    areaValue: 280,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    type: 'rent',
    propertyType: 'Penthouse'
  }, {
    id: 12,
    title: 'Cozy 2BR Apartment',
    price: '18,000 EGP/month',
    priceValue: 18000,
    location: 'New Cairo, 5th Settlement',
    beds: 2,
    baths: 2,
    area: '120 sqm',
    areaValue: 120,
    image: 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&q=80',
    type: 'rent',
    propertyType: 'Apartment'
  }]);
  const toggleFavorite = (propertyId: number) => {
    setFavorites(prev => prev.includes(propertyId) ? prev.filter(id => id !== propertyId) : [...prev, propertyId]);
  };
  const handleAddProperty = () => {
    setShowAddPropertyModal(true);
  };
  const handleCloseAddProperty = () => {
    setShowAddPropertyModal(false);
    // Reset form
    setTransactionType('Sale');
    setLocation('');
    setPropertyGroup('Residential');
    setPropertyType('Apartments');
    setPropertyStage('New');
    setPropertyView('');
    setFinishedType('');
    setPropertySize('');
    setBeds('');
    setBaths('');
    setInCompound(false);
    setIsReadyToMove(false);
    setEstimatedPrice('');
    setIsInsurance(false);
    setIsInstallments(false);
    setDescription('');
    setPropertyImages([]);
    setSelectedPropertyAmenities([]);
    setSelectedCommunityAmenities([]);
  };
  const handleSubmitProperty = () => {
    alert('Property added successfully!');
    handleCloseAddProperty();
  };
  const togglePropertyAmenity = (amenity: string) => {
    setSelectedPropertyAmenities(prev => prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]);
  };
  const toggleCommunityAmenity = (amenity: string) => {
    setSelectedCommunityAmenities(prev => prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]);
  };
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(async position => {
      const {
        latitude,
        longitude
      } = position.coords;
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
        const data = await response.json();
        const locationName = data.address.suburb || data.address.city || data.address.town || data.address.village || 'Current Location';
        setLocation(locationName);
      } catch (error) {
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }
      setIsLoadingLocation(false);
    }, error => {
      setIsLoadingLocation(false);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          alert('Location access denied. Please enable location permissions in your browser settings.');
          break;
        case error.POSITION_UNAVAILABLE:
          alert('Location information is unavailable.');
          break;
        case error.TIMEOUT:
          alert('Location request timed out.');
          break;
        default:
          alert('An error occurred while getting your location.');
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  const handleClearFilters = () => {
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterBeds('');
    setFilterBaths('');
    setFilterPropertyType('');
    setFilterMinArea('');
    setFilterMaxArea('');
    setFilterLocation('');
  };
  const handleApplyFilters = () => {
    setShowAdvancedFilters(false);
  };
  const hasActiveFilters = filterMinPrice || filterMaxPrice || filterBeds || filterBaths || filterPropertyType || filterMinArea || filterMaxArea || filterLocation;
  const activeFilterCount = [filterMinPrice || filterMaxPrice, filterBeds, filterBaths, filterPropertyType, filterMinArea || filterMaxArea, filterLocation].filter(Boolean).length;
  const propertyAmenities = ['Balcony/Terrace', "Maid's Room", 'Central Air Conditioning', 'Private Pool', 'Built-in Wardrobes', 'Fully Fitted Kitchen', 'Furnished/Unfurnished', 'High-Speed Internet', 'Smart Home Technology', 'Pet-Friendly'];
  const communityAmenities = ['Gymnasium', 'Shared Swimming Pool', "Children's Play Area", 'BBQ Area', '24/7 Security', 'Covered Parking', 'Landscaped Gardens', 'Tennis/Squash Courts', 'Sauna/Steam Room', 'Retail Shops', 'Jogging/Cycling Tracks', 'Community Hall', 'Public Transport Access', 'Sports Area', 'Beach Hotel', 'Beach Club', 'Boutique Hotel', 'Sports Clubs', 'Schools', 'Hospital', 'Retail', 'Infrastructure', 'Lagoon', 'Smart Home Technology', 'Furnished/Unfurnished', 'Built-in Wardrobes', 'High-Speed Internet', 'Private Pool', "Maid's Room", 'Balcony/Terrace', 'Pet-Friendly', 'Central Air Conditioning', 'Fully Fitted Kitchen'];
  const filteredProperties = properties.filter(property => {
    // Type filter (sale/rent)
    if (property.type !== activeTab) return false;
    // Search query
    if (searchQuery && !property.title.toLowerCase().includes(searchQuery.toLowerCase()) && !property.location.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Price filter
    if (filterMinPrice && property.priceValue < parseInt(filterMinPrice)) {
      return false;
    }
    if (filterMaxPrice && property.priceValue > parseInt(filterMaxPrice)) {
      return false;
    }
    // Beds filter
    if (filterBeds) {
      if (filterBeds === '5+' && property.beds < 5) return false;
      if (filterBeds !== '5+' && property.beds !== parseInt(filterBeds)) return false;
    }
    // Baths filter
    if (filterBaths) {
      if (filterBaths === '5+' && property.baths < 5) return false;
      if (filterBaths !== '5+' && property.baths !== parseInt(filterBaths)) return false;
    }
    // Property type filter
    if (filterPropertyType && property.propertyType.toLowerCase() !== filterPropertyType.toLowerCase()) {
      return false;
    }
    // Area filter
    if (filterMinArea && property.areaValue < parseInt(filterMinArea)) {
      return false;
    }
    if (filterMaxArea && property.areaValue > parseInt(filterMaxArea)) {
      return false;
    }
    // Location filter
    if (filterLocation && !property.location.toLowerCase().includes(filterLocation.toLowerCase())) {
      return false;
    }
    return true;
  });
  const handleTabChange = (tab: 'home' | 'clients' | 'properties') => {
    if (tab === 'home' && onDashboardClick) {
      onDashboardClick();
    } else if (tab === 'clients' && onClientsClick) {
      onClientsClick();
    }
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-2721">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-2722">
        <TopBar data-id="element-2723" />
        <MainTabs activeTab="properties" onTabChange={handleTabChange} data-id="element-2724" />

        {/* Search Bar with Filter Button */}
        <div className="px-6 pt-4 pb-2" data-id="element-2725">
          <div className="flex gap-2" data-id="element-2726">
            <div className="relative flex-1" data-id="element-2727">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2728" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search properties or location" className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary shadow-sm" data-id="element-2729" />
            </div>
            <button onClick={() => setShowAdvancedFilters(true)} className={`px-4 py-3 rounded-xl border-2 transition-colors flex items-center gap-2 ${hasActiveFilters ? 'bg-secondary border-secondary text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-secondary'}`} data-id="element-2730">
              <SlidersHorizontal className="w-5 h-5" data-id="element-2731" />
              {hasActiveFilters && activeFilterCount > 0 && <span className="text-xs font-bold bg-white text-secondary rounded-full w-5 h-5 flex items-center justify-center" data-id="element-2732">
                  {activeFilterCount}
                </span>}
            </button>
          </div>
        </div>

        {/* Add Property Button */}
        <div className="px-6 pb-2" data-id="element-2733">
          <button onClick={handleAddProperty} className="w-full bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-2734">
            <Plus className="w-5 h-5" data-id="element-2735" />
            Add New Property
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 pb-3 bg-white" data-id="element-2736">
          <div className="flex gap-3" data-id="element-2737">
            <button onClick={() => setActiveTab('sale')} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'sale' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-2738">
              For Sale ({properties.filter(p => p.type === 'sale').length})
            </button>
            <button onClick={() => setActiveTab('rent')} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'rent' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-2739">
              For Rent ({properties.filter(p => p.type === 'rent').length})
            </button>
          </div>
        </div>

        {/* Properties Grid */}
        <div className="px-4 pb-4" data-id="element-2740">
          <div className="grid grid-cols-1 gap-4" data-id="element-2741">
            {filteredProperties.length > 0 ? filteredProperties.map(property => <div key={property.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2742">
                  {/* Property Image */}
                  <div className="relative h-48" data-id="element-2743">
                    <img src={property.image} alt={property.title} className="w-full h-full object-cover" data-id="element-2744" />
                    <button onClick={() => toggleFavorite(property.id)} className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors" data-id="element-2745">
                      <Heart className={`w-4 h-4 ${favorites.includes(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} data-id="element-2746" />
                    </button>
                    <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white text-sm font-semibold px-3 py-1.5 rounded-lg" data-id="element-2747">
                      {property.price}
                    </div>
                  </div>

                  {/* Property Info */}
                  <div className="p-4" data-id="element-2748">
                    <h3 className="font-bold text-neutral-text text-base mb-2" data-id="element-2749">
                      {property.title}
                    </h3>

                    <div className="flex items-center text-gray-500 text-sm mb-3" data-id="element-2750">
                      <MapPin className="w-4 h-4 mr-1 flex-shrink-0" data-id="element-2751" />
                      <span className="line-clamp-1" data-id="element-2752">{property.location}</span>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-2753">
                      <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-2754">
                        <Bed className="w-4 h-4 text-gray-400" data-id="element-2755" />
                        <span data-id="element-2756">{property.beds}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-2757">
                        <Bath className="w-4 h-4 text-gray-400" data-id="element-2758" />
                        <span data-id="element-2759">{property.baths}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-2760">
                        <Ruler className="w-4 h-4 text-gray-400" data-id="element-2761" />
                        <span data-id="element-2762">{property.area}</span>
                      </div>
                      <button onClick={() => onPropertyClick && onPropertyClick(property.id)} className="text-sm font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-2763">
                        View Details
                      </button>
                    </div>
                  </div>
                </div>) : <div className="flex flex-col items-center justify-center py-16 px-4" data-id="element-2764">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-2765">
                  <Home className="w-8 h-8 text-gray-400" data-id="element-2766" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-text mb-1" data-id="element-2767">
                  No Properties Found
                </h3>
                <p className="text-sm text-gray-500 text-center" data-id="element-2768">
                  {searchQuery || hasActiveFilters ? 'Try adjusting your search or filters' : `No properties available for ${activeTab === 'sale' ? 'sale' : 'rent'} yet.`}
                </p>
              </div>}
          </div>
        </div>

        {/* Advanced Filters Modal */}
        {showAdvancedFilters && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2769">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-2770">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-2771">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-2772">
                  Filter Properties
                </h2>
                <button onClick={() => setShowAdvancedFilters(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-2773">
                  <X className="w-5 h-5" data-id="element-2774" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2775">
                {/* Price Range */}
                <div data-id="element-2776">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2777">
                    <DollarSign className="w-4 h-4 text-secondary" data-id="element-2778" />
                    Price Range
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2779">
                    <div data-id="element-2780">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-2781">
                        Min Price
                      </label>
                      <div className="relative" data-id="element-2782">
                        <input type="number" value={filterMinPrice} onChange={e => setFilterMinPrice(e.target.value)} placeholder="0" className="w-full px-3 py-2.5 pr-14 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2783" />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" data-id="element-2784">
                          EGP
                        </span>
                      </div>
                    </div>
                    <div data-id="element-2785">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-2786">
                        Max Price
                      </label>
                      <div className="relative" data-id="element-2787">
                        <input type="number" value={filterMaxPrice} onChange={e => setFilterMaxPrice(e.target.value)} placeholder="0" className="w-full px-3 py-2.5 pr-14 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2788" />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" data-id="element-2789">
                          EGP
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bedrooms */}
                <div data-id="element-2790">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2791">
                    <Bed className="w-4 h-4 text-secondary" data-id="element-2792" />
                    Bedrooms
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-2793">
                    {['1', '2', '3', '4', '5+'].map(bed => <button key={bed} onClick={() => setFilterBeds(filterBeds === bed ? '' : bed)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${filterBeds === bed ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-2794">
                        {filterBeds === bed && <Check className="w-3 h-3 inline mr-1" data-id="element-2795" />}
                        {bed}
                      </button>)}
                  </div>
                </div>

                {/* Bathrooms */}
                <div data-id="element-2796">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2797">
                    <Bath className="w-4 h-4 text-secondary" data-id="element-2798" />
                    Bathrooms
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-2799">
                    {['1', '2', '3', '4', '5+'].map(bath => <button key={bath} onClick={() => setFilterBaths(filterBaths === bath ? '' : bath)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${filterBaths === bath ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-2800">
                        {filterBaths === bath && <Check className="w-3 h-3 inline mr-1" data-id="element-2801" />}
                        {bath}
                      </button>)}
                  </div>
                </div>

                {/* Property Type */}
                <div data-id="element-2802">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2803">
                    <Building2 className="w-4 h-4 text-secondary" data-id="element-2804" />
                    Property Type
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-2805">
                    {['Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Duplex', 'Studio'].map(type => <button key={type} onClick={() => setFilterPropertyType(filterPropertyType === type ? '' : type)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${filterPropertyType === type ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-2806">
                        {filterPropertyType === type && <Check className="w-3 h-3 inline mr-1" data-id="element-2807" />}
                        {type}
                      </button>)}
                  </div>
                </div>

                {/* Area Range */}
                <div data-id="element-2808">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2809">
                    <Ruler className="w-4 h-4 text-secondary" data-id="element-2810" />
                    Area Range
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2811">
                    <div data-id="element-2812">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-2813">
                        Min Area
                      </label>
                      <div className="relative" data-id="element-2814">
                        <input type="number" value={filterMinArea} onChange={e => setFilterMinArea(e.target.value)} placeholder="0" className="w-full px-3 py-2.5 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2815" />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" data-id="element-2816">
                          m²
                        </span>
                      </div>
                    </div>
                    <div data-id="element-2817">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-2818">
                        Max Area
                      </label>
                      <div className="relative" data-id="element-2819">
                        <input type="number" value={filterMaxArea} onChange={e => setFilterMaxArea(e.target.value)} placeholder="0" className="w-full px-3 py-2.5 pr-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2820" />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" data-id="element-2821">
                          m²
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div data-id="element-2822">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-2823">
                    <MapPin className="w-4 h-4 text-secondary" data-id="element-2824" />
                    Location
                  </label>
                  <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-2825">
                    <option value="" data-id="element-2826">All Locations</option>
                    <option value="New Cairo" data-id="element-2827">New Cairo</option>
                    <option value="5th Settlement" data-id="element-2828">5th Settlement</option>
                    <option value="Madinaty" data-id="element-2829">Madinaty</option>
                    <option value="Rehab" data-id="element-2830">Rehab City</option>
                    <option value="Hyde Park" data-id="element-2831">Hyde Park</option>
                    <option value="Waterway" data-id="element-2832">Waterway</option>
                  </select>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-2833">
                <div className="flex gap-3" data-id="element-2834">
                  <button onClick={handleClearFilters} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-colors" data-id="element-2835">
                    Clear All
                  </button>
                  <button onClick={handleApplyFilters} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-3 rounded-xl transition-colors" data-id="element-2836">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Add Property Modal - unchanged */}
        {showAddPropertyModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2837">
            <div className="bg-white w-full sm:max-w-4xl rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-2838">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-2839">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-2840">
                  Add New Property
                </h2>
                <button onClick={handleCloseAddProperty} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-2841">
                  <X className="w-5 h-5" data-id="element-2842" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2843">
                {/* Transaction Type - Radio Buttons */}
                <div data-id="element-2844">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2845">
                    Transaction Type
                  </label>
                  <div className="flex gap-4" data-id="element-2846">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2847">
                      <input type="radio" name="transactionType" value="Sale" checked={transactionType === 'Sale'} onChange={e => setTransactionType(e.target.value)} className="w-5 h-5 text-secondary border-gray-300 focus:ring-secondary" data-id="element-2848" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2849">
                        Sale
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2850">
                      <input type="radio" name="transactionType" value="Rent" checked={transactionType === 'Rent'} onChange={e => setTransactionType(e.target.value)} className="w-5 h-5 text-secondary border-gray-300 focus:ring-secondary" data-id="element-2851" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2852">
                        Rent
                      </span>
                    </label>
                  </div>
                </div>

                {/* Location - Search Input */}
                <div data-id="element-2853">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2854">
                    Select Location
                  </label>
                  <div className="space-y-2" data-id="element-2855">
                    <div className="relative" data-id="element-2856">
                      <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2857" />
                      <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Search location..." className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2858" />
                    </div>
                    <button type="button" onClick={handleUseCurrentLocation} disabled={isLoadingLocation} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2859">
                      <Navigation className={`w-4 h-4 ${isLoadingLocation ? 'animate-spin' : ''}`} data-id="element-2860" />
                      {isLoadingLocation ? 'Getting location...' : 'Use Current Location'}
                    </button>
                  </div>
                </div>

                {/* 2 Fields Per Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6" data-id="element-2861">
                  {/* Property Group */}
                  <div data-id="element-2862">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2863">
                      Property Group
                    </label>
                    <select value={propertyGroup} onChange={e => setPropertyGroup(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-2864">
                      <option value="Residential" data-id="element-2865">Residential</option>
                      <option value="Commercial" data-id="element-2866">Commercial</option>
                      <option value="Administrative" data-id="element-2867">Administrative</option>
                    </select>
                  </div>

                  {/* Property Type */}
                  <div data-id="element-2868">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2869">
                      Property Type
                    </label>
                    <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-2870">
                      <option value="Apartments" data-id="element-2871">Apartments</option>
                      <option value="Villa" data-id="element-2872">Villa</option>
                      <option value="Townhouse" data-id="element-2873">Townhouse</option>
                      <option value="Penthouse" data-id="element-2874">Penthouse</option>
                      <option value="Duplex" data-id="element-2875">Duplex</option>
                      <option value="Studio" data-id="element-2876">Studio</option>
                    </select>
                  </div>

                  {/* Property View */}
                  <div data-id="element-2877">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2878">
                      Property View
                    </label>
                    <select value={propertyView} onChange={e => setPropertyView(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-2879">
                      <option value="" data-id="element-2880">Select View</option>
                      <option value="Garden View" data-id="element-2881">Garden View</option>
                      <option value="Pool View" data-id="element-2882">Pool View</option>
                      <option value="Street View" data-id="element-2883">Street View</option>
                      <option value="Landscape View" data-id="element-2884">Landscape View</option>
                      <option value="Golf View" data-id="element-2885">Golf View</option>
                    </select>
                  </div>

                  {/* Finished Type */}
                  <div data-id="element-2886">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2887">
                      Finished Type
                    </label>
                    <select value={finishedType} onChange={e => setFinishedType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-2888">
                      <option value="" data-id="element-2889">Select Type</option>
                      <option value="Fully Finished" data-id="element-2890">Fully Finished</option>
                      <option value="Semi Finished" data-id="element-2891">Semi Finished</option>
                      <option value="Core & Shell" data-id="element-2892">Core & Shell</option>
                      <option value="Furnished" data-id="element-2893">Furnished</option>
                    </select>
                  </div>

                  {/* Property Size */}
                  <div data-id="element-2894">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2895">
                      Property Size
                    </label>
                    <div className="relative" data-id="element-2896">
                      <input type="number" value={propertySize} onChange={e => setPropertySize(e.target.value)} placeholder="Enter size" className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2897" />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" data-id="element-2898">
                        m²
                      </span>
                    </div>
                  </div>

                  {/* Beds */}
                  <div data-id="element-2899">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2900">
                      Beds
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-2901">
                      {['Studio', '1', '2', '3', '4', '5', '6+'].map(bedOption => <button key={bedOption} type="button" onClick={() => setBeds(bedOption)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all border-2 min-w-[60px] ${beds === bedOption ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-2902">
                            {bedOption}
                          </button>)}
                    </div>
                  </div>

                  {/* Baths */}
                  <div data-id="element-2903">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2904">
                      Baths
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-2905">
                      {['1', '2', '3', '4', '5', '6+'].map(bathOption => <button key={bathOption} type="button" onClick={() => setBaths(bathOption)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all border-2 min-w-[60px] ${baths === bathOption ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-2906">
                          {bathOption}
                        </button>)}
                    </div>
                  </div>
                </div>

                {/* Property Features */}
                <div data-id="element-2907">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2908">
                    Property Features
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2909">
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-2910">
                      <input type="checkbox" checked={inCompound} onChange={e => setInCompound(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2911" />
                      <span className="text-sm text-gray-700" data-id="element-2912">In Compound</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-2913">
                      <input type="checkbox" checked={isReadyToMove} onChange={e => setIsReadyToMove(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2914" />
                      <span className="text-sm text-gray-700" data-id="element-2915">
                        Is Ready To Move
                      </span>
                    </label>
                  </div>
                </div>

                {/* Estimated Price */}
                <div data-id="element-2916">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2917">
                    Estimated Price
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2918">
                    <div className="md:col-span-1" data-id="element-2919">
                      <div className="relative" data-id="element-2920">
                        <input type="text" value={estimatedPrice} onChange={e => setEstimatedPrice(e.target.value)} placeholder="Estimated Price" className="w-full px-4 py-3 pr-16 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2921" />
                        <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-semibold" data-id="element-2922">
                          EGP
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-2 flex gap-4" data-id="element-2923">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-2924">
                        <input type="checkbox" checked={isInsurance} onChange={e => setIsInsurance(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2925" />
                        <span className="text-sm text-gray-700" data-id="element-2926">
                          Is Insurance
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-2927">
                        <input type="checkbox" checked={isInstallments} onChange={e => setIsInstallments(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2928" />
                        <span className="text-sm text-gray-700" data-id="element-2929">
                          Is Installments
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div data-id="element-2930">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2931">
                    Description
                  </h3>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter property description..." rows={6} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none" data-id="element-2932" />
                </div>

                {/* Property Images */}
                <div data-id="element-2933">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2934">
                    Property Images
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2935">
                    {/* Camera Option */}
                    <div className="border-2 border-dashed border-blue-200 bg-blue-50 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-blue-100 transition-all cursor-pointer group" data-id="element-2936">
                      <div className="flex flex-col items-center gap-3" data-id="element-2937">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform" data-id="element-2938">
                          <Camera className="w-8 h-8 text-blue-500" data-id="element-2939" />
                        </div>
                        <div data-id="element-2940">
                          <p className="text-base font-bold text-blue-900 mb-1" data-id="element-2941">
                            Take Photo
                          </p>
                          <p className="text-sm text-blue-600/80" data-id="element-2942">
                            Use your camera
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Upload Option */}
                    <div className="border-2 border-dashed border-gray-300 bg-gray-50 rounded-xl p-6 text-center hover:border-secondary hover:bg-gray-100 transition-all cursor-pointer group" data-id="element-2943">
                      <div className="flex flex-col items-center gap-3" data-id="element-2944">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform" data-id="element-2945">
                          <ImageIcon className="w-8 h-8 text-gray-400 group-hover:text-secondary" data-id="element-2946" />
                        </div>
                        <div data-id="element-2947">
                          <p className="text-base font-bold text-neutral-text mb-1" data-id="element-2948">
                            Upload Files
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-2949">
                            or drag and drop
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property Amenities */}
                <div data-id="element-2950">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2951">
                    Property Amenities
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-2952">
                    {propertyAmenities.map(amenity => <label key={amenity} className="flex items-center gap-2 cursor-pointer" data-id="element-2953">
                        <input type="checkbox" checked={selectedPropertyAmenities.includes(amenity)} onChange={() => togglePropertyAmenity(amenity)} className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2954" />
                        <span className="text-sm text-gray-700" data-id="element-2955">{amenity}</span>
                      </label>)}
                  </div>
                </div>

                {/* Community Amenities */}
                <div data-id="element-2956">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-2957">
                    Community Amenities
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-2958">
                    {communityAmenities.map(amenity => <label key={amenity} className="flex items-center gap-2 cursor-pointer" data-id="element-2959">
                        <input type="checkbox" checked={selectedCommunityAmenities.includes(amenity)} onChange={() => toggleCommunityAmenity(amenity)} className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-2960" />
                        <span className="text-sm text-gray-700" data-id="element-2961">{amenity}</span>
                      </label>)}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-2962">
                <div className="flex gap-3" data-id="element-2963">
                  <button onClick={handleCloseAddProperty} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 rounded-xl transition-colors" data-id="element-2964">
                    Cancel
                  </button>
                  <button onClick={handleSubmitProperty} disabled={!location || !propertySize || !estimatedPrice} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2965">
                    Save Property
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onProfileClick={onProfileClick} data-id="element-2966" />
      </div>
    </div>;
}